import React, { useContext, useEffect, useRef, useState } from 'react';
import { fromEvent, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap, throttleTime } from 'rxjs/operators';
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

export const CoordinatedScrollAnchorContext = React.createContext<{ anchorEl: HTMLElement }>(null);

export function HdCoordinatedScrollAnchor({ elRef, children }) {
  const [state, setState] = useState<{ anchorEl: HTMLElement }>({ anchorEl: elRef });

  useEffect(() => {
    setState(prevState => ({ ...prevState, anchorEl: elRef }));
  }, [elRef]);
  return (
    <CoordinatedScrollAnchorContext.Provider value={state}>
      {children}
    </CoordinatedScrollAnchorContext.Provider>
  );
}

export const CoordinatedScrollContext = React.createContext<{ el: HTMLElement }>(null);

export function HdCoordinatedScroll({ elRef, children }) {
  const [state, setState] = useState<{ el: HTMLElement }>({ el: elRef }); // state can hold any data not just el

  useEffect(() => {
    setState(prevState => ({ ...prevState, el: elRef }));
  }, [elRef]);

  const subRef = useRef(Subscription.EMPTY);

  const reactCoordinatedScrollParent = useContext(CoordinatedScrollContext)?.el;

  const reactAnchor = useContext(CoordinatedScrollAnchorContext)?.anchorEl;

  const parentEl = reactCoordinatedScrollParent;
  const anchorEl = reactAnchor;

  const scrollParentToAnchor = scrollTop => {
    const el = parentEl;

    const offsetFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;

    if (offsetFromBottom) {
      el.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    subRef.current.unsubscribe();

    if (!parentEl || !anchorEl || !elRef) {
      return () => {};
    }

    subRef.current = fromEvent(elRef, 'wheel')
      .pipe(
        throttleTime(1000),
        switchMap((e: any) =>
          fromEvent(elRef, 'scroll').pipe(
            take(1),
            map(() => elRef.scrollTop),
            filter(offset => offset > 0 && parentEl.scrollTop < anchorEl.offsetTop),
            tap(dir => {
              e.preventDefault();
              scrollParentToAnchor(anchorEl.offsetTop);
            })
          )
        )
      )
      .subscribe();

    return () => {
      subRef.current.unsubscribe();
    };
  }, [parentEl, anchorEl, elRef]);

  return (
    <CoordinatedScrollContext.Provider value={state}>{children}</CoordinatedScrollContext.Provider>
  );
}
